import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Loader2, Save, CalendarClock, PartyPopper, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPeriod = { name: "", kind: "period", start_date: "", end_date: "", price_per_night: 150, min_nights: 0, active: true };

const AdminPricing = ({ authHeaders }) => {
  const [settings, setSettings] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyPeriod);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/pricing-settings`, { headers: authHeaders }),
        axios.get(`${API}/admin/pricing-periods`, { headers: authHeaders }),
      ]);
      setSettings(s.data);
      setPeriods(p.data.periods || []);
    } catch {
      toast.error("Impossibile caricare la configurazione prezzi");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/pricing-settings`, {
        ...settings,
        weekend_value: Number(settings.weekend_value) || 0,
        last_minute_window_days: Number(settings.last_minute_window_days) || 7,
        last_minute_pct: Number(settings.last_minute_pct) || 0,
        last_minute_min_nights: Number(settings.last_minute_min_nights) || 2,
      }, { headers: authHeaders });
      toast.success("Impostazioni salvate");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  const addPeriod = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) { toast.error("Compila nome, inizio e fine"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/pricing-periods`, {
        ...form,
        price_per_night: Number(form.price_per_night),
        min_nights: Number(form.min_nights) || 0,
      }, { headers: authHeaders });
      toast.success("Periodo creato");
      setForm(emptyPeriod);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Creazione fallita");
    } finally { setSaving(false); }
  };

  const togglePeriod = async (row) => {
    try {
      await axios.patch(`${API}/admin/pricing-periods/${row.id}`, { active: !row.active }, { headers: authHeaders });
      load();
    } catch { toast.error("Aggiornamento fallito"); }
  };

  const removePeriod = async (row) => {
    if (!window.confirm(`Eliminare "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pricing-periods/${row.id}`, { headers: authHeaders });
      load();
    } catch { toast.error("Eliminazione fallita"); }
  };

  if (loading || !settings) {
    return <p className="text-sm text-[#5C6773] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</p>;
  }

  const inp = "w-full px-3 py-2 rounded-lg bg-white border border-[#0D253A]/15 text-sm";
  const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  return (
    <div data-testid="studio-pricing" className="space-y-8">
      {/* Weekend & Last Minute settings */}
      <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-[#C86D51]" />
            <h3 className="font-display text-lg text-[#0D253A]">Prezzo weekend</h3>
          </div>
          <label className="inline-flex items-center gap-2 text-sm mb-3">
            <input data-testid="pricing-weekend-enabled" type="checkbox" checked={settings.weekend_enabled}
              onChange={(e) => setSettings({ ...settings, weekend_enabled: e.target.checked })} />
            Attiva supplemento weekend
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {dayNames.map((n, i) => {
              const on = (settings.weekend_days || []).includes(i);
              return (
                <button key={n} type="button" onClick={() => {
                  const cur = new Set(settings.weekend_days || []);
                  if (on) cur.delete(i); else cur.add(i);
                  setSettings({ ...settings, weekend_days: [...cur].sort() });
                }} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#0D253A] text-white border-[#0D253A]" : "border-[#0D253A]/20 text-[#5C6773]"}`}>
                  {n}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Tipo</label>
              <select value={settings.weekend_kind} onChange={(e) => setSettings({ ...settings, weekend_kind: e.target.value })} className={inp}>
                <option value="percent">+ %</option>
                <option value="amount">+ € fisso</option>
                <option value="absolute">Prezzo pieno €</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Valore</label>
              <input data-testid="pricing-weekend-value" type="number" step="0.01" value={settings.weekend_value}
                onChange={(e) => setSettings({ ...settings, weekend_value: e.target.value })} className={inp} />
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#C86D51]" />
            <h3 className="font-display text-lg text-[#0D253A]">Ultimo minuto</h3>
          </div>
          <label className="inline-flex items-center gap-2 text-sm mb-3">
            <input data-testid="pricing-lm-enabled" type="checkbox" checked={settings.last_minute_enabled}
              onChange={(e) => setSettings({ ...settings, last_minute_enabled: e.target.checked })} />
            Attiva sconto ultimo minuto
          </label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Finestra (gg)</label>
              <input data-testid="pricing-lm-window" type="number" min="1" max="60" value={settings.last_minute_window_days}
                onChange={(e) => setSettings({ ...settings, last_minute_window_days: e.target.value })} className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Sconto %</label>
              <input data-testid="pricing-lm-pct" type="number" min="0" max="100" step="0.5" value={settings.last_minute_pct}
                onChange={(e) => setSettings({ ...settings, last_minute_pct: e.target.value })} className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Min notti</label>
              <input type="number" min="1" value={settings.last_minute_min_nights}
                onChange={(e) => setSettings({ ...settings, last_minute_min_nights: e.target.value })} className={inp} />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={settings.last_minute_exclude_specials}
              onChange={(e) => setSettings({ ...settings, last_minute_exclude_specials: e.target.checked })} />
            Escludi periodi speciali / eventi
          </label>
        </div>
        <div className="lg:col-span-2 flex justify-end">
          <button data-testid="pricing-save-settings" onClick={saveSettings} disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
          </button>
        </div>
      </div>

      {/* Periodi speciali & eventi */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <form onSubmit={addPeriod} className="lg:col-span-2 bg-white rounded-2xl border border-[#0D253A]/10 p-6 space-y-3">
          <div className="flex items-center gap-2">
            <PartyPopper className="w-4 h-4 text-[#C86D51]" />
            <h3 className="font-display text-lg text-[#0D253A]">Nuovo periodo / evento</h3>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Nome</label>
            <input data-testid="pricing-period-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} placeholder="es. Ferragosto, Alta stagione" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Tipo</label>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inp}>
                <option value="period">Periodo</option>
                <option value="event">Evento</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Prezzo/notte €</label>
              <input data-testid="pricing-period-price" type="number" step="0.01" min="0" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Da</label>
              <input data-testid="pricing-period-from" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">A</label>
              <input data-testid="pricing-period-to" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inp} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Min. notti (facoltativo)</label>
            <input type="number" min="0" value={form.min_nights} onChange={(e) => setForm({ ...form, min_nights: e.target.value })} className={inp} />
          </div>
          <button data-testid="pricing-period-create" type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#0D253A] text-white text-sm font-semibold rounded-full hover:bg-[#C86D51] disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi
          </button>
          <p className="text-xs text-[#5C6773]">In caso di sovrapposizione tra periodi, vince quello con data di inizio più recente. Le notti di periodo/evento sostituiscono base e weekend.</p>
        </form>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-[#0D253A]/10 p-6">
          <h3 className="font-display text-lg text-[#0D253A] mb-3">Periodi ed eventi</h3>
          {periods.length === 0 ? (
            <p data-testid="pricing-periods-empty" className="text-sm text-[#5C6773]">Nessun periodo speciale configurato.</p>
          ) : (
            <div className="space-y-2">
              {periods.map((row) => (
                <div key={row.id} data-testid={`pricing-period-row-${row.id.slice(0, 8)}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3 ${row.active ? "border-[#0D253A]/10" : "border-[#0D253A]/10 opacity-50"}`}>
                  <div>
                    <p className="font-semibold text-[#0D253A]">
                      {row.name}
                      <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${row.kind === "event" ? "bg-[#C86D51]/15 text-[#C86D51]" : "bg-[#1E5F74]/15 text-[#1E5F74]"}`}>
                        {row.kind === "event" ? "Evento" : "Periodo"}
                      </span>
                    </p>
                    <p className="text-xs text-[#5C6773]">{row.start_date} → {row.end_date} · € {Number(row.price_per_night).toFixed(2)}/notte {row.min_nights ? `· min ${row.min_nights} notti` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`pricing-period-toggle-${row.id.slice(0, 8)}`} onClick={() => togglePeriod(row)} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0D253A]/20 hover:border-[#C86D51] hover:text-[#C86D51]">
                      {row.active ? "Disattiva" : "Attiva"}
                    </button>
                    <button data-testid={`pricing-period-delete-${row.id.slice(0, 8)}`} onClick={() => removePeriod(row)} className="p-1.5 rounded-full border border-[#0D253A]/20 hover:border-[#C86D51] hover:text-[#C86D51]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;
